import * as React from "react";
import { cn } from "../lib/utils";

interface AdminTableContainerProps extends React.HTMLAttributes<HTMLElement> {
  // Required: every scrollable region needs its own name to be told apart in assistive tech.
  "aria-label": string;
}

// The bare scrolling shell, for tables that bring their own <table> and sizing.
function AdminTableContainer({
  className,
  style,
  ref,
  ...props
}: AdminTableContainerProps & { ref?: React.Ref<HTMLElement> }) {
  return (
    <section
      ref={ref}
      className={cn("admin-table-container", className)}
      style={style}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- WCAG SC 2.1.1: scrollable region must be keyboard-focusable; axe rule scrollable-region-focusable. <section> with aria-label provides a discoverable region name.
      tabIndex={0}
      {...props}
    />
  );
}

interface AdminTableProps extends React.HTMLAttributes<HTMLDivElement> {
  maxHeight?: string;
  rounded?: boolean;
  children: React.ReactNode;
}

function AdminTable({
  maxHeight = "500px",
  rounded = true,
  className,
  children,
  ref,
  "aria-label": ariaLabel,
  ...props
}: AdminTableProps & { ref?: React.Ref<HTMLDivElement> }) {
  // WCAG SC 2.1.1 (Keyboard): a scrollable region must be reachable by keyboard so users can
  // scroll it. axe rule: scrollable-region-focusable. Adding tabIndex makes the
  // container focusable; role="region" + aria-label give it a discoverable name
  // in assistive tech.
  return (
    <AdminTableContainer
      ref={ref}
      className={cn("overflow-y-auto", className)}
      style={{ maxHeight, borderRadius: rounded ? undefined : 0 }}
      aria-label={ariaLabel ?? "Scrollable table"}
      {...props}
    >
      <table className="admin-table">{children}</table>
    </AdminTableContainer>
  );
}

function AdminTableHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <thead className={cn("sticky top-0 z-10 font-sans", className)} {...props}>
      {children}
    </thead>
  );
}

function AdminTableBody({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLTableSectionElement>) {
  return (
    <tbody className={cn(className)} {...props}>
      {children}
    </tbody>
  );
}

export { AdminTable, AdminTableBody, AdminTableContainer, AdminTableHeader };
